import { useGroupLicensePermissions } from '@group/License/useGroupLicensePermissions';
import { forwardRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  faCaretDown,
  faCaretRight,
  faPen,
  faPlus,
  faPaste,
  faFolderPlus,
  faFolderOpen,
  faFolderTree,
} from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Workspace } from '@workspace/domain/workspace';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { SidebarMenuItem } from './SidebarMenuItem';
import { useCreateViewModelFromClipboardPayloadHandler } from '@workspace/view-model-folder/hooks';
import { ViewModelCreateModal } from '@workspace/view-model-folder/ui/ViewModelCreateModal';
import { FolderEditModal } from '@workspace/view-model-folder/ui/FolderEditModal';
import { SidebarMenuButton } from './SidebarMenuButton';
import { FolderDeleteModal } from '@workspace/view-model-folder/ui/FolderDeleteModal';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { toast } from 'react-hot-toast';
import { useCurrentUserId } from '@framework/auth';
import { FolderCreateModal } from '@workspace/view-model-folder/ui/FolderCreateModal';
import { MoveFolderModal } from '@workspace/view-model-folder/ui/MoveModal/MoveFolderModal';
import { MoveToOtherWorkspaceModal } from '@workspace/view-model-folder/ui/MoveToOtherWorkspaceModal';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';
import { FolderItemTitle } from './FolderItemTitle';
import { setScrolledViewModelAtom } from '../SidebarTree/SidebarTreeStore/sidebarTreeControlState';
import { useSetAtom } from 'jotai';
import { openFoldersAtom } from '../SidebarTree/SidebarTreeStore/sidebarTreeOpenState';

type Modals =
  'ViewModelCreate' | 'FolderCreate' | 'FolderEdit' | 'FolderDelete' | 'MoveFolder' | 'MoveToOtherWorkspace';

const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewModelCreateModalOpen: useCallback(() => {
      setOpenModal('ViewModelCreate');
    }, []),
    handleFolderCreateModalOpen: useCallback(() => {
      setOpenModal('FolderCreate');
    }, []),
    handleFolderEditModalOpen: useCallback(() => {
      setOpenModal('FolderEdit');
    }, []),
    handleFolderDeleteModalOpen: useCallback(() => {
      setOpenModal('FolderDelete');
    }, []),
    handleMoveFolderModalOpen: useCallback(() => {
      setOpenModal('MoveFolder');
    }, []),
    handleMoveToOtherWorkspaceModalOpen: useCallback(() => {
      setOpenModal('MoveToOtherWorkspace');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  workspace: Workspace;
  folder: Folder;
  folderTree: RootFolderTree;
  toggleFolder: () => void;
  isOpen: boolean;
  isSelected: boolean;
  firstLoaded?: boolean;
  searchKeyword?: string;
  children?: React.ReactNode;
};

export const FolderItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      workspace,
      folder,
      folderTree,
      toggleFolder,
      isOpen,
      isSelected,
      firstLoaded = true,
      searchKeyword,
      children,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation();
    const actionLogSender = useActionLogSender();

    const { isEditMenuButtonShowable } = useSidebarItemMenuPermissions(workspace);

    const {
      openModal,
      handleViewModelCreateModalOpen,
      handleFolderCreateModalOpen,
      handleFolderEditModalOpen,
      handleFolderDeleteModalOpen,
      handleMoveFolderModalOpen,
      handleMoveToOtherWorkspaceModalOpen,
      handleModalClose,
    } = useHandleModals();

    const currentUserId = useCurrentUserId();
    const canDuplicateFiles = useGroupLicensePermissions()?.fileUpload !== false;

    const handleDuplicateFolder = useCallback(async () => {
      if (!currentUserId) return;

      try {
        let hasError = false;
        const onError = (message: string) => {
          hasError = true;
          toast.error(message);
        };
        toast(t('workspace.folder.duplicate.progress'), { id: folder.id, duration: Infinity });
        await ViewModelOperation.duplicateFolder(
          folder.id,
          currentUserId,
          onError,
          actionLogSender,
          (name) => t('canvas.viewModel.folderCopyName', { name }),
          canDuplicateFiles
        );
        if (!hasError) toast.success(t('workspace.folder.duplicate.success'));
      } catch (e) {
        toast.error(t('workspace.folder.duplicate.error'));
        throw e;
      } finally {
        toast.dismiss(folder.id);
      }
    }, [currentUserId, folder.id, actionLogSender, t, canDuplicateFiles]);

    const setScrolledViewModel = useSetAtom(setScrolledViewModelAtom);
    const openFolders = useSetAtom(openFoldersAtom);

    const handleFolderSubmit = useCallback(
      async (name: string) => {
        const viewModelId = await ViewModelOperation.create(workspace.id, folder.id, name, actionLogSender);
        setScrolledViewModel(viewModelId);
        openFolders({ folderIds: [folder.id], workspaceId: workspace.id });
        handleModalClose();
      },
      [workspace.id, folder.id, actionLogSender, setScrolledViewModel, openFolders, handleModalClose]
    );

    const createViewModelFromClipboardPayload = useCreateViewModelFromClipboardPayloadHandler(
      workspace.groupId,
      workspace.id,
      folder.id
    );

    return (
      <div className="relative" ref={ref}>
        <div className="flex items-center">
          <button className="mr-1 focus:outline-none" onClick={toggleFolder}>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
          </button>
          <SidebarMenuItem
            isSelected={isSelected}
            isMenuButtonShowable={isEditMenuButtonShowable}
            popupMenu={
              <>
                <SidebarMenuButton
                  handleClick={handleViewModelCreateModalOpen}
                  icon={faPlus}
                  title={t('sidebar.items.menu.newViewModel')}
                />
                <SidebarMenuButton
                  handleClick={handleFolderCreateModalOpen}
                  icon={faFolderPlus}
                  title={t('sidebar.items.menu.newFolder')}
                />
                <SidebarMenuButton
                  handleClick={handleDuplicateFolder}
                  icon={faCopy}
                  title={t('workspace.folder.duplicate.label')}
                />
                <SidebarMenuButton
                  handleClick={createViewModelFromClipboardPayload}
                  icon={faPaste}
                  title={t('sidebar.items.menu.pasteViewModel')}
                />
                <SidebarMenuButton
                  handleClick={handleMoveFolderModalOpen}
                  icon={faFolderOpen}
                  title={t('workspace.folder.move.label')}
                />
                <SidebarMenuButton
                  handleClick={handleMoveToOtherWorkspaceModalOpen}
                  icon={faFolderTree}
                  title={t('workspace.moveToAnother.label')}
                />
                <SidebarMenuButton
                  handleClick={handleFolderEditModalOpen}
                  icon={faPen}
                  title={t('common.action.edit')}
                />
                <SidebarMenuButton
                  handleClick={handleFolderDeleteModalOpen}
                  icon={faTrashAlt}
                  title={t('common.action.delete')}
                  className="text-red-700"
                />
              </>
            }
          >
            <FolderItemTitle
              workspaceId={workspace.id}
              folderId={folder.id}
              folderName={folder.name}
              searchKeyword={searchKeyword}
            />
          </SidebarMenuItem>
        </div>

        {openModal === 'ViewModelCreate' && (
          <ViewModelCreateModal
            isOpen={openModal === 'ViewModelCreate'}
            onSubmit={handleFolderSubmit}
            onClose={handleModalClose}
          />
        )}

        {openModal === 'FolderCreate' && (
          <FolderCreateModal
            isOpen={openModal === 'FolderCreate'}
            onClose={handleModalClose}
            workspaceId={workspace.id}
            folderId={folder.id}
          />
        )}

        {openModal === 'FolderEdit' && (
          <FolderEditModal folder={folder} isOpen={openModal === 'FolderEdit'} onClose={handleModalClose} />
        )}

        {openModal === 'FolderDelete' && (
          <FolderDeleteModal
            folder={folder}
            isOpen={openModal === 'FolderDelete'}
            onClose={handleModalClose}
            hasChildren={folderTree.hasChildrenOf(folder.id)}
          />
        )}

        {openModal === 'MoveFolder' && (
          <MoveFolderModal isOpen={openModal === 'MoveFolder'} onClose={handleModalClose} folder={folder} />
        )}

        {currentUserId && openModal === 'MoveToOtherWorkspace' && (
          <MoveToOtherWorkspaceModal
            isOpen={openModal === 'MoveToOtherWorkspace'}
            onClose={handleModalClose}
            folder={folder}
            userId={currentUserId}
            groupId={workspace.groupId}
            workspaceId={workspace.id}
          />
        )}

        {isOpen && firstLoaded && <div className="ml-4">{children}</div>}
      </div>
    );
  }
);

FolderItem.displayName = 'FolderItem';
